import { defineStore } from "pinia";
import {
  InteractionEvent,
  InteractionPool,
  MateMail,
  MateRelationship,
} from "@/types/entity";
import {
  ApiGetInteractionPoolPage,
  ApiGetInteractionEventPage,
  ApiGetMateMailPage,
  ApiGetMateRelationshipPage,
  ApiPostInteractionPool,
  ApiDeleteInteractionPool,
  ApiPutMateRelationship,
  ApiPostMateRelationship,
  ApiPostMateRelationshipImportAdmire,
  ApiGetMateMail,
  ApiPostMateMail,
  ApiPutMateMail,
  ApiPostMateMailTrash,
  ApiPostMateMailRead,
  ApiPostMateInvitation,
  ApiGetMateInvitation,
  ApiPostMateInvitationRespond,
  ApiPostMateInvitationCancel,
  ApiDeleteMateRelationship,
} from "@/apis/interaction";
import { showLoadingToast, showSuccessToast, showToast } from "vant";
import i18n from "@/locales";
import { RelationshipType, MailSenderStatus } from "@/enums/mission";
import { MateMailType } from "@/enums/mateEnums";
import { useGlobalUser } from "./globalUser";

export const useInteractive = defineStore("interactive", {
  state: () => ({
    poolList: [] as InteractionPool[],
    eventList: [] as InteractionEvent[],
    mailList: [] as MateMail[],
    relationshipList: [] as MateRelationship[],
    myRelationshipList: [] as MateRelationship[],
    myRelationshipOriginList: [] as MateRelationship[],
    estimatedPowerCubes: 0,
    currentRelationship: null as MateRelationship | null,
    currentMail: null as MateMail | null,

    homeNeedReload: false,
  }),
  getters: {
    mateId2PoolMap: (state) => {
      return new Map(state.poolList.map((v) => [v.mateId, v]));
    },
    isPoolEnabled: () => {
      const userStore = useGlobalUser();
      return userStore.user.interactionPoolStatus === 1;
    },
  },
  actions: {
    async getPoolList() {
      const { page } = await ApiGetInteractionPoolPage({
        pagination: {
          page: 1,
          perPage: 999,
        },
      });
      this.poolList = page.data;
      this.estimatedPowerCubes = page.additional.estimatedPowerCubes;
    },

    async addToPool(mateId: number, spriteId?: number) {
      await ApiPostInteractionPool({
        mateId,
        spriteId,
      });
      this.getPoolList();
    },

    async removeFromPool(id: number) {
      await ApiDeleteInteractionPool(id);
      // this.poolList = this.poolList.filter((v) => v.id !== id);
      await this.getPoolList();
    },

    getRelationshipTypeName(relationship?: MateRelationship) {
      if (!relationship) return "";
      const { type, isBlocked } = relationship;
      if (isBlocked) return i18n.global.t("interactive.relationshipBlocked");
      switch (type) {
        case RelationshipType.Acquaintance:
          return i18n.global.t("interactive.relationshipAcquaintance");
        case RelationshipType.Friend:
          return i18n.global.t("interactive.relationshipFriend");
        case RelationshipType.CustomRelationship:
          return (
            relationship.name ||
            i18n.global.t("interactive.relationshipUnnamed")
          );
        default:
          return i18n.global.t("interactive.relationshipUnknown");
      }
    },

    async getEventList() {
      const { page } = await ApiGetInteractionEventPage({
        pagination: {
          page: 1,
          perPage: 999,
        },
      });
      this.eventList = page.data;
    },

    async getMailList(params: {
      page: number;
      mateId?: number;
      type: MateMailType;
      otherMateId?: number;
    }) {
      const { page, mateId, type, otherMateId } = params;
      try {
        const { page: res } = await ApiGetMateMailPage({
          pagination: {
            page,
            perPage: 5,
          },
          filter: {
            type,
            mateId,
            otherMateIds: otherMateId ? [otherMateId] : undefined,
          },
          sort: { field: "dtUpdate", order: "desc" },
        });
        this.mailList = [...this.mailList, ...res.data];
        return res.data;
      } catch (error) {
        console.error("Failed to fetch mails", error);
        showToast("Failed to load mails");
        return [];
      }
    },

    async getRelationshipList(
      options: {
        targetType: "mine" | "others";
        sourceMateId?: number;
      } = { targetType: "others" }
    ) {
      try {
        const { page } = await ApiGetMateRelationshipPage({
          pagination: {
            page: 1,
            perPage: 999,
          },
          sort: { field: "dtUpdate", order: "desc" },
          filter: options,
        });

        const relationshipList = this.handleReverseRelationship(page.data);

        if (options.targetType === "mine") {
          this.myRelationshipList = relationshipList;
          this.myRelationshipOriginList = page.data;
        } else {
          this.relationshipList = relationshipList;
        }
      } catch (error) {
        console.error("Failed to fetch relationships", error);
        showToast("Failed to load relationships");
      }
    },

    handleReverseRelationship(relationship: MateRelationship[]) {
      const cacheMap: Record<string, MateRelationship> = {};

      relationship.forEach((v) => {
        if (cacheMap[`${v.targetMateId}-${v.sourceMateId}`]) {
          cacheMap[`${v.targetMateId}-${v.sourceMateId}`].reverse = v;
        } else {
          cacheMap[`${v.sourceMateId}-${v.targetMateId}`] = v;
        }
      });

      return Object.values(cacheMap);
    },

    async updateRelationship(
      id: number,
      params: Parameters<typeof ApiPutMateRelationship>[1]
    ) {
      try {
        const response = await ApiPutMateRelationship(id, params);

        // Update both lists
        this.relationshipList = this.relationshipList.map((r) =>
          r.id === id ? { ...r, ...params } : r
        );

        this.myRelationshipList = this.myRelationshipList.map((r) =>
          r.id === id ? { ...r, ...params } : r
        );

        if (this.currentRelationship && this.currentRelationship.id === id) {
          this.currentRelationship = { ...this.currentRelationship, ...params };
        }

        return response;
      } catch (error) {
        console.error("Failed to update relationship", error);
        showToast("Failed to update relationship");
        throw error;
      }
    },

    async createRelationship(
      sourceMateId: number,
      targetMateId: number,
      targetType: "mine" | "others" = "mine"
    ) {
      const { item } = await ApiPostMateRelationship({
        name: "",
        description: "",
        sourceMateId,
        targetMateId,
      });
      await ApiPostMateRelationship({
        name: "",
        description: "",
        sourceMateId: targetMateId,
        targetMateId: sourceMateId,
      });
      await this.getRelationshipList({ targetType });
      return item;
    },

    async forgetRelationship(id: number) {
      await ApiDeleteMateRelationship(id);
      this.relationshipList = this.relationshipList.filter((v) => v.id !== id);
      this.myRelationshipList = this.myRelationshipList.filter(
        (v) => v.id !== id
      );
    },

    async importOC(mateId: number) {
      const { itemzz } = await ApiPostMateRelationshipImportAdmire({
        mateId,
      });
      await this.getRelationshipList({ targetType: "others" });
      return itemzz;
    },

    async getMail(uuid: string): Promise<MateMail | null> {
      try {
        const mail = this.mailList.find((v) => v.uuid === uuid);
        if (mail) {
          this.currentMail = mail;
          return mail;
        }

        const res = await ApiGetMateMail(uuid);
        this.currentMail = res.item;
        return this.currentMail;
      } catch (error) {
        console.error("Failed to fetch mail", error);
        showToast("Failed to load mail details");
        return null;
      }
    },

    async createMail(params: Parameters<typeof ApiPostMateMail>[0]) {
      try {
        const response = await ApiPostMateMail(params);
        await this.getMailList({
          page: 1,
          type:
            params.senderStatus === MailSenderStatus.Sent
              ? MateMailType.Sent
              : MateMailType.Draft,
        });
        return response.item;
      } catch (error) {
        console.error("Failed to create mail", error);
        showToast("Failed to create mail");
        throw error;
      }
    },

    async updateMail(id: number, params: Parameters<typeof ApiPutMateMail>[1]) {
      try {
        const response = await ApiPutMateMail(id, params);

        // Update mail list if status changed
        if (params.senderStatus) {
          await this.getMailList({
            page: 1,
            type:
              params.senderStatus === MailSenderStatus.Sent
                ? MateMailType.Sent
                : MateMailType.Draft,
          });
        }

        if (this.currentMail && this.currentMail.id === id) {
          this.currentMail = { ...this.currentMail, ...params };
        }

        return response;
      } catch (error) {
        console.error("Failed to update mail", error);
        showToast("Failed to update mail");
        throw error;
      }
    },

    async readMail(id: number) {
      await ApiPostMateMailRead(id);
    },

    async deleteMail(id: number) {
      try {
        await ApiPostMateMailTrash(id);
        this.mailList = this.mailList.filter((m) => m.id !== id);
        if (this.currentMail && this.currentMail.id === id) {
          this.currentMail = null;
        }
      } catch (error) {
        console.error("Failed to delete mail", error);
        showToast("Failed to delete mail");
        throw error;
      }
    },

    async createInvitation(
      params: Parameters<typeof ApiPostMateInvitation>[0]
    ) {
      const response = await ApiPostMateInvitation(params);
      return response.item;
    },

    async getInvitation(uuid: string) {
      const response = await ApiGetMateInvitation(uuid);
      return response.item;
    },

    async respondInvitation(
      id: number,
      params: Parameters<typeof ApiPostMateInvitationRespond>[1]
    ) {
      const response = await ApiPostMateInvitationRespond(id, params);
      return response.item;
    },

    async cancelFriend(id: number) {
      await ApiPostMateInvitationCancel(id);
      await this.getRelationshipList({ targetType: "others" });
    },
    async toggleUserInteractionPoolStatus() {
      const loading = showLoadingToast("");
      try {
        const userStore = useGlobalUser();
        await userStore.updateUserApi({
          interactionPoolStatus:
            userStore.user.interactionPoolStatus === 1 ? 2 : 1,
        });
      } finally {
        loading.close();
      }
    },
  },
});
